import React, { useState, useMemo } from 'react';
import {
  Popover,
  Box,
  Avatar,
  Typography,
  Button,
  Divider,
  Stack,
  Switch,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Logout,
  Brightness4,
  Brightness7,
  TextFields,
  Add,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useAuthStore } from '../stores/authStore';
import { Routes as AppRoutes } from '../../../routes';
import OrganizationDisplay from '../../organisation/components/OrganizationDisplay';
import { useThemeStore } from '../../../shared/stores/themeStore';
import { useDensityStore } from '../../../shared/stores/densityStore';
import { useMultiAccountStore } from '../stores/multiAccountStore';
import { FeatureSwitchGate, Features } from '@billda/features-ui';
import { AccountListItem, isTokenExpired } from '@billda/shared-ui';
import { LogoutOptionsDialog } from './LogoutOptionsDialog';

interface UserMenuPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

export const UserMenuPopover: React.FC<UserMenuPopoverProps> = ({
  anchorEl,
  onClose,
}) => {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { logout, logoutAll, switchAccount } = useAuthStore();
  const { mode, toggleMode } = useThemeStore();
  const { density, toggleDensity } = useDensityStore();
  const accountsRaw = useMultiAccountStore(state => state.accounts);
  const currentAccountEmail = useMultiAccountStore(
    state => state.currentAccountEmail
  );

  const [switching, setSwitching] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  // Sort accounts by last used
  const accounts = useMemo(
    () =>
      [...accountsRaw].sort(
        (a, b) =>
          new Date(b.lastUsedAt).getTime() - new Date(a.lastUsedAt).getTime()
      ),
    [accountsRaw]
  );

  const handleOpenLogoutDialog = () => {
    setLogoutDialogOpen(true);
  };

  const handleCloseLogoutDialog = () => {
    setLogoutDialogOpen(false);
  };

  const handleLogout = () => {
    console.log('👤 User initiated logout from UserMenuPopover');
    setLogoutDialogOpen(false);
    onClose();
    navigate(AppRoutes.login);
    logout('manual_user_action');
  };

  const handleLogoutAll = () => {
    console.log('👤 User initiated logout all from UserMenuPopover');
    setLogoutDialogOpen(false);
    onClose();
    navigate(AppRoutes.login);
    logoutAll('manual_user_action_all_devices');
  };

  const handleSwitchAccount = async (email: string) => {
    if (email === currentAccountEmail) {
      return; // Already on this account
    }

    setSwitching(true);

    try {
      await switchAccount(email);
      onClose();
      // Page will reload with new account's data
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch account:', err);
      setSwitching(false);
      // Error will be shown by the AccountListItem (expired badge)
    }
  };

  const handleAddAccount = () => {
    onClose();
    navigate(`${AppRoutes.login}?addAccount=true`);
  };

  const checkTokenExpired = (refreshToken: string): boolean => {
    const expired = isTokenExpired(refreshToken);
    return expired === true;
  };

  const open = Boolean(anchorEl);

  if (!currentUser) {
    return null;
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      data-testid="user-menu-popover"
      slotProps={{
        paper: {
          sx: {
            width: 320,
            mt: 0.5,
          },
        },
      }}
    >
      {/* User Info Section */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          sx={{
            width: 56,
            height: 56,
            bgcolor: 'background.paper',
            color: 'primary.main',
            fontSize: '1.25rem',
            fontWeight: 600,
            border: theme => `2px solid ${theme.palette.divider}`,
          }}
        >
          {currentUser.initials}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {currentUser.name}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {currentUser.email}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Organization Section */}
      <OrganizationDisplay />

      <Divider />

      {/* Account List Section - feature gated */}
      <FeatureSwitchGate
        feature={Features.Auth.IsMultiAccountManagementEnabled}
      >
        <Box
          sx={{
            maxHeight: accounts.length > 3 ? '320px' : 'auto', // 4 items * 80px = 320px
            overflowY: accounts.length > 3 ? 'auto' : 'visible',
          }}
        >
          <List sx={{ pt: 0, pb: 0 }}>
            {accounts.map(account => {
              const expired = checkTokenExpired(account.refreshToken);
              const isCurrent = account.email === currentAccountEmail;

              return (
                <AccountListItem
                  key={account.email}
                  account={account}
                  isCurrent={isCurrent}
                  isExpired={expired}
                  onClick={handleSwitchAccount}
                  disabled={switching || isCurrent || expired}
                />
              );
            })}

            {/* Add Account List Item */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleAddAccount}
                disabled={switching}
                data-testid="add-new-account-list-item"
                sx={{ py: 2 }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    bgcolor: 'action.hover',
                    color: 'primary.main',
                  }}
                >
                  <Add sx={{ fontSize: 20 }} />
                </Box>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Add New Account
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Sign in with a different email
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>

        {switching && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 2,
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ ml: 2 }} color="text.secondary">
              Switching account...
            </Typography>
          </Box>
        )}

        <Divider />
      </FeatureSwitchGate>

      {/* Theme Toggle Section */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {mode === 'light' ? (
              <Brightness7
                sx={{ color: 'text.secondary', fontSize: '1.25rem' }}
              />
            ) : (
              <Brightness4
                sx={{ color: 'text.secondary', fontSize: '1.25rem' }}
              />
            )}
            <Typography variant="body2" color="text.primary">
              Dark Mode
            </Typography>
          </Box>
          <Switch
            checked={mode === 'dark'}
            onChange={toggleMode}
            size="small"
            data-testid="user-menu-theme-toggle"
          />
        </Stack>
      </Box>

      <Divider />

      {/* Density Toggle Section */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextFields sx={{ color: 'text.secondary', fontSize: '1.25rem' }} />
            <Typography variant="body2" color="text.primary">
              Comfortable
            </Typography>
          </Box>
          <Switch
            checked={density === 'comfortable'}
            onChange={toggleDensity}
            size="small"
            data-testid="user-menu-density-toggle"
          />
        </Stack>
      </Box>

      <Divider />

      {/* Logout Button */}
      <Box sx={{ p: 1 }}>
        <Button
          fullWidth
          startIcon={<Logout />}
          onClick={handleOpenLogoutDialog}
          data-testid="user-menu-logout"
          sx={{
            justifyContent: 'flex-start',
            px: 2,
            py: 1,
            color: 'text.primary',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          Sign out
        </Button>
      </Box>

      {/* Logout Options Dialog */}
      <LogoutOptionsDialog
        open={logoutDialogOpen}
        onClose={handleCloseLogoutDialog}
        onLogout={handleLogout}
        onLogoutAll={handleLogoutAll}
      />
    </Popover>
  );
};
